import { fetchApi } from './client';

/**
 * Attaching a file, through the shop's own upload route and no other.
 *
 * `POST /api/v1/uploads` is where this platform already stores a tenant's media: it sniffs the type,
 * refuses anything a browser would not render as an image, caps one file at eight megabytes and one
 * request at ten of them. A form field must not be able to attach what that route would refuse, so the
 * same three limits are named here and checked before a byte leaves the browser — the server still
 * enforces them, and the browser only spares the visitor an upload that was always going to fail.
 */

/** Exactly the types the upload route stores; an SVG is deliberately not among them, because it can script. */
export const UPLOAD_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'] as const;

/** The per-file ceiling the route is configured with, in bytes. */
export const UPLOAD_MAX_BYTES = 8 * 1024 * 1024;

/** The most files one request may carry, which is the route's own limit on the `files` part. */
export const UPLOAD_MAX_FILES = 10;

/** What a file picker may offer, so the operating system's own dialogue filters to what the shop takes. */
export const UPLOAD_ACCEPT = UPLOAD_TYPES.join(',');

/** One stored file, as the upload route answers it. */
export type UploadedFile = { url: string; filename: string; size: number };

// Reports why the shop's upload route would refuse one file, or null when it would store it.
export function uploadRefusal(file: File): string | null {
  if (!(UPLOAD_TYPES as readonly string[]).includes(file.type)) {
    return `${file.name}: only JPEG, PNG, WebP, GIF and AVIF images can be attached`;
  }
  if (file.size > UPLOAD_MAX_BYTES) return `${file.name}: larger than the 8 MB limit`;
  return null;
}

// Reads the stored files out of an answer whose rows the route builds itself, dropping anything unnamed.
const storedFiles = (rows: readonly UploadedFile[]): readonly UploadedFile[] =>
  rows.filter((row) => typeof row?.url === 'string' && row.url.length > 0);

// Sends files to the shop's own upload route and answers the addresses it stored them under.
export async function uploadStoreFiles(files: readonly File[]): Promise<readonly UploadedFile[]> {
  const body = new FormData();
  for (const file of files.slice(0, UPLOAD_MAX_FILES)) body.append('files', file);
  return storedFiles(await fetchApi<UploadedFile[]>('/uploads', { method: 'POST', body }));
}
